import { useEffect } from "react"
import { Flex } from "@shared/primitive-components"
import { RadioGroupAction, RadioGroupState, RootContextType } from "./types"
import { createContextOnce, createProviderForContext, useContext, useCreateReducer, useReducer } from "../utils"

type RootOwnProps<T> = Readonly<{
  value?: T
  defaultValue?: T
  disabled?: boolean
  onValueChange?: (value: T) => void
  children?: React.ReactNode
}>

export type RootProps<T> = RootOwnProps<T> & React.ComponentPropsWithRef<"div">

const createRadioGroupContext = createContextOnce()
const useRadioGroup = <T,>(consumerName: string) =>
  useContext(createRadioGroupContext<RootContextType<T>>(), consumerName)

// eslint-disable-next-line react/function-component-definition
const Root = function <T>({ disabled, value, defaultValue, onValueChange, children }: RootProps<T>) {
  const context = createRadioGroupContext<RootContextType<T>>()
  const Provider = createProviderForContext<RootContextType<T>>(context)

  const reducer = useCreateReducer<RadioGroupState<T>, RadioGroupAction<T>>((state, action) => {
    switch (action.type) {
      case `setValue`:
        return {
          ...state,
          value: action.value,
        }
      default:
        throw new Error(`No action with the type: ${action.type}`)
    }
  })

  const [state, dispatch] = useReducer<RadioGroupState<T>, RadioGroupAction<T>>(reducer, {
    disabled: disabled ?? false,
    value: value ?? defaultValue,
  })

  useEffect(() => {
    if (onValueChange && state.value && !disabled) {
      onValueChange(state.value)
    }
  }, [state.value, disabled, onValueChange])

  useEffect(() => {
    if (value !== undefined) {
      dispatch({ type: `setValue`, value })
    }
  }, [value])

  useEffect(() => {
    if (defaultValue !== undefined) {
      dispatch({ type: `setValue`, value: defaultValue })
    }
  }, [defaultValue])

  return (
    <Provider state={state} dispatch={dispatch}>
      <Flex role="radiogroup">{children}</Flex>
    </Provider>
  )
}

Root.displayName = `RadioGroup`

export { Root, useRadioGroup }
